import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SlidersHorizontal, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import SettingsForm from "@/components/SettingsForm";
import ResultsDisplay from "@/components/ResultsDisplay";
import EventGuidance from "@/components/EventGuidance";
import GearRecommendation from "@/components/GearRecommendation";
import GaleriaSocialCard from "@/components/GaleriaSocialCard";
import FoundationsCard from "@/components/FoundationsCard";
import AIShotForm from "@/components/AIShotForm";
import AIResultsDisplay from "@/components/AIResultsDisplay";

const API = "/api";

function ModeToggle({ mode, onChange }) {
  const { t } = useTranslation();
  const options = [
    { value: "manual", labelKey: "mode_manual", Icon: SlidersHorizontal },
    { value: "ai", labelKey: "mode_ai", Icon: Sparkle },
  ];
  return (
    <div
      className="flex gap-1 p-1 bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl"
      data-testid="mode-toggle"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
            mode === opt.value
              ? "bg-[#F5A524] text-[#0A0A0A]"
              : "bg-transparent text-[#A1A1AA] hover:text-white"
          )}
          data-testid={`mode-toggle-${opt.value}`}
        >
          <opt.Icon weight={opt.value === "ai" ? "fill" : "regular"} className="w-4 h-4" />
          {t(opt.labelKey)}
        </button>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const [mode, setMode] = useState("manual");
  const [results, setResults] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gear, setGear] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const { user } = useAuth();
  const { t, lang } = useTranslation();

  useEffect(() => {
    if (user && user !== false) {
      axios
        .get(`${API}/gear`, { withCredentials: true })
        .then((res) => setGear(res.data))
        .catch(() => {});
    } else {
      setGear(null);
    }
  }, [user]);

  const handleCalculate = async (data) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/calculate`, data, {
        withCredentials: true,
      });
      setResults(res.data);
      setFormData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!user || user === false) {
      toast.error(t("login_required"));
      return;
    }
    try {
      await axios.post(
        `${API}/history`,
        { settings_input: formData, results },
        { withCredentials: true }
      );
      toast.success(t("res_saved"));
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t("login_required"));
      } else {
        toast.error(t("error"));
      }
    }
  };

  const handleAiSubmit = async (description, timeOfDay, setting) => {
    if (!description || !description.trim()) {
      toast.error(t("ai_empty_error"));
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai-shot`,
        { description: description.trim(), lang, time_of_day: timeOfDay || null, setting: setting || null },
        { withCredentials: true }
      );
      setAiResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("ai_error"));
    }
    setAiLoading(false);
  };

  const handleApplyToManual = () => {
    if (!aiResult || !aiResult.manual_form) return;
    // new object reference each time so SettingsForm's effect re-fires
    setPrefill({ ...aiResult.manual_form, _ts: Date.now() });
    setMode("manual");
    setResults(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(t("ai_applied"));
  };

  const hasGear =
    gear &&
    ((gear.cameras && gear.cameras.length > 0) ||
      (gear.lenses && gear.lenses.length > 0));

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
      data-testid="dashboard"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <ModeToggle mode={mode} onChange={setMode} />
          {mode === "manual" ? (
            <SettingsForm
              key={prefill?._ts || "manual"}
              onCalculate={handleCalculate}
              loading={loading}
              gear={gear}
              prefill={prefill}
            />
          ) : (
            <AIShotForm onSubmit={handleAiSubmit} loading={aiLoading} hasGear={hasGear} />
          )}
        </div>
        <div className="lg:col-span-7">
          {mode === "manual" ? (
            <>
              {results && gear && (
                <div className="flex items-center gap-2 mb-3" data-testid="ideal-setup-label">
                  <span className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold">
                    {t("gr_ideal_label")}
                  </span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
              )}
              <ResultsDisplay
                results={results}
                onSave={handleSave}
                formData={formData}
                isAuthenticated={user && user !== false}
              />
              {results && (
                <EventGuidance
                  eventScenario={results.event_scenario}
                  primaryGoal={results.primary_goal}
                />
              )}
              {results && gear && (
                <GearRecommendation results={results} gear={gear} isAuthenticated={user && user !== false} />
              )}
              {results && !gear && user && user !== false && (
                <GearRecommendation results={results} gear={null} isAuthenticated={true} />
              )}
            </>
          ) : (
            <AIResultsDisplay result={aiResult} loading={aiLoading} onApplyToManual={handleApplyToManual} />
          )}
        </div>
      </div>

      <div className="mt-8 space-y-6">
        <FoundationsCard />
        <GaleriaSocialCard />
      </div>
    </div>
  );
}
